(() => {
  if (customElements.get('shop-tv-player')) return;
  let pendingConsent = null;

  function clearConsentIntent(intent = pendingConsent) {
    if (!intent || pendingConsent !== intent) return;
    pendingConsent = null;
    intent.controller.abort();
    intent.observer?.disconnect();
    clearTimeout(intent.timer);
    clearTimeout(intent.closeTimer);
    intent.cancelClose?.();
    if (intent.player.cmpHandoff === intent) intent.player.cmpHandoff = null;
    intent.player.playRequested = false;
  }

  class ShopTvPlayer extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.media = this.querySelector('deferred-media');
      this.poster = this.querySelector('[data-tv-poster]');
      this.startButton = this.querySelector('[data-tv-start]');
      this.consentPanel = this.querySelector('[data-tv-consent-panel]');
      this.overlay = this.querySelector('.tv-player__overlay');
      if (this.overlay && this.consentPanel && this.consentPanel.parentElement !== this.overlay) {
        this.overlay.appendChild(this.consentPanel);
      }
      this.cookieSettings = this.querySelector('[data-tv-cookie-settings]');
      this.withdrawButton = this.querySelector('[data-tv-withdraw]');
      this.status = this.querySelector('[data-tv-status]');
      this.dialog = this.closest('dialog');
      const options = { signal: this.controller.signal };
      this.addEventListener('click', event => this.onClick(event), options);
      this.dialog?.addEventListener('close', () => {
        const preserve = pendingConsent?.player === this && this.cmpHandoff === pendingConsent;
        this.cmpHandoff = null;
        this.unload(false, preserve);
      }, options);
      this.dialog?.addEventListener('cancel', () => this.unload(false), options);
      window.addEventListener('pagehide', () => this.unload(false), options);
      document.addEventListener('visitorConsentCollected', event => {
        const intent = pendingConsent;
        if (intent?.player === this && event.detail?.marketingAllowed === true) {
          if (!intent.sawVisible) this.checkConsentIntent(intent);
          if (pendingConsent === intent && intent.sawVisible) {
            intent.granted = true;
            this.checkConsentIntent(intent);
          }
          return;
        }
        if (event.detail?.marketingAllowed === false || !this.privacyAllowed()) {
          const wasActive = this.media?.hasAttribute('loaded');
          this.unload(false);
          if (wasActive) this.setStatus(this.dataset.blockedMessage);
        }
      }, options);
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.unload(false);
    }

    onClick(event) {
      const action = event.target.closest('[data-tv-start], [data-tv-cancel], [data-tv-withdraw], [data-tv-cookie-settings]');
      if (!action || !this.contains(action)) return;
      if (action.hasAttribute('data-tv-start')) {
        clearConsentIntent();
        this.playRequested = true;
        this.loadExternal();
      }
      else if (action.hasAttribute('data-tv-cookie-settings')) this.openCookieSettings();
      else this.unload(true);
    }

    setStatus(message) {
      if (this.status) this.status.textContent = message || '';
    }

    showConsent(show) {
      if (this.consentPanel) {
        this.consentPanel.hidden = !show;
        if (!show) this.consentPanel.scrollTop = 0;
      }
      if (this.startButton) {
        this.startButton.hidden = show;
        this.startButton.setAttribute('aria-expanded', String(show));
      }
      this.classList.toggle('tv-player--consent', show);
    }

    focusConsentAction(action) {
      action?.focus();
      action?.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'instant' });
    }

    privacyAllowed() {
      try { return window.Shopify?.customerPrivacy?.marketingAllowed() === true; }
      catch { return false; }
    }

    async ensurePrivacy() {
      if (window.Shopify?.customerPrivacy) return true;
      if (!window.Shopify?.loadFeatures) return false;
      return new Promise(resolve => {
        let settled = false;
        const finish = error => {
          if (settled) return;
          settled = true;
          clearTimeout(timer);
          resolve(!error && Boolean(window.Shopify?.customerPrivacy));
        };
        const timer = setTimeout(() => finish(true), 4000);
        try { window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.2' }], finish); }
        catch { finish(true); }
      });
    }

    async openCookieSettings() {
      const shelf = this.closest('shop-tv-shelf');
      if (!this.playRequested || !this.dialog?.open || typeof shelf?.open !== 'function' || typeof window.privacyBanner?.showPreferences !== 'function') {
        this.setStatus(this.dataset.blockedMessage);
        return;
      }
      const opener = shelf.returnTargets?.get(this.dialog);
      clearConsentIntent();
      const intent = {
        player: this, dialog: this.dialog, shelf, opener,
        controller: new AbortController(), openDeadline: 0,
        saved: false, granted: false, sawVisible: false
      };
      pendingConsent = intent;
      this.playRequested = true;
      this.unload(false, true);
      this.cmpHandoff = intent;
      const closed = await new Promise(resolve => {
        let settled = false;
        const finish = result => {
          if (settled) return;
          settled = true;
          clearTimeout(intent.closeTimer);
          resolve(result);
        };
        intent.cancelClose = () => finish(false);
        intent.dialog.addEventListener('close', () => finish(true), { once: true, signal: intent.controller.signal });
        intent.closeTimer = setTimeout(() => finish(false), 2000);
        intent.dialog.close();
      });
      intent.cancelClose = null;
      if (!closed || pendingConsent !== intent || !this.isConnected || intent.dialog.open || document.querySelector('dialog.stv-dialog[open]')) {
        clearConsentIntent(intent);
        return;
      }
      intent.generation = this.generation;
      const options = { capture: true, signal: intent.controller.signal };
      document.addEventListener('click', event => {
        if (!(event.target instanceof Element)) return;
        if (event.target.closest('[data-tv-open], [data-tv-open-slug], #shopify-pc__prefs__header-close')) {
          clearConsentIntent(intent);
        } else if (event.target.closest('#shopify-pc__prefs__header-save')) {
          intent.saved = true;
        }
      }, options);
      document.addEventListener('keydown', event => {
        if (event.key === 'Escape') clearConsentIntent(intent);
      }, options);
      window.addEventListener('hashchange', () => clearConsentIntent(intent), { signal: intent.controller.signal });
      intent.openDeadline = performance.now() + 4000;
      try {
        const opened = window.privacyBanner.showPreferences();
        opened?.catch(() => clearConsentIntent(intent));
        this.checkConsentIntent(intent);
      } catch { clearConsentIntent(intent); }
    }

    checkConsentIntent(intent) {
      if (pendingConsent !== intent) return;
      clearTimeout(intent.timer);
      if (!this.isConnected || !this.controller || this.controller.signal.aborted || !intent.shelf.isConnected ||
          intent.generation !== this.generation || intent.dialog.open || document.querySelector('dialog.stv-dialog[open]')) {
        clearConsentIntent(intent);
        return;
      }
      const prefs = intent.prefs || document.getElementById('shopify-pc__prefs');
      if (intent.prefs && !intent.prefs.isConnected) {
        clearConsentIntent(intent);
        return;
      }
      if (prefs && !intent.prefs) {
        intent.prefs = prefs;
        intent.observer = new MutationObserver(() => this.checkConsentIntent(intent));
        intent.observer.observe(prefs, { attributes: true, attributeFilter: ['style', 'class', 'hidden', 'aria-hidden'] });
        for (let ancestor = prefs.parentElement; ancestor; ancestor = ancestor.parentElement) {
          intent.observer.observe(ancestor, { childList: true });
        }
      }
      const style = prefs?.isConnected ? getComputedStyle(prefs) : null;
      const visible = Boolean(prefs && !prefs.hidden && style?.display !== 'none' && style?.visibility !== 'hidden' && prefs.getClientRects().length);
      if (visible) {
        intent.sawVisible = true;
        return;
      }
      if (!intent.sawVisible) {
        if (performance.now() >= intent.openDeadline) clearConsentIntent(intent);
        else intent.timer = setTimeout(() => this.checkConsentIntent(intent), 50);
        return;
      }
      if (!intent.saved && !intent.granted) {
        clearConsentIntent(intent);
        return;
      }
      intent.decisionDeadline ||= performance.now() + 2500;
      if (intent.granted && this.privacyAllowed()) {
        const { dialog, shelf, opener } = intent;
        clearConsentIntent(intent);
        if (!this.isConnected || !dialog.isConnected || !shelf.isConnected || document.querySelector('dialog.stv-dialog[open]')) return;
        shelf.open(dialog, opener?.isConnected ? opener : undefined);
        if (dialog.open) {
          this.playRequested = true;
          this.loadExternal();
        }
      } else if (performance.now() >= intent.decisionDeadline) {
        clearConsentIntent(intent);
      } else {
        intent.timer = setTimeout(() => this.checkConsentIntent(intent), 50);
      }
    }

    async loadExternal() {
      if (!this.startButton || this.startButton.disabled || this.media?.hasAttribute('loaded') || !this.media?.querySelector('template')) return;
      const generation = this.generation = (this.generation || 0) + 1;
      this.startButton.disabled = true;
      if (this.cookieSettings) this.cookieSettings.hidden = true;
      this.setStatus(this.dataset.loadingMessage);
      let privacyReady = true;
      if (!window.Shopify?.customerPrivacy) {
        this.showConsent(true);
        this.focusConsentAction(this.querySelector('[data-tv-cancel]'));
        privacyReady = await this.ensurePrivacy();
      }
      if (!this.isConnected || generation !== this.generation || (this.dialog && !this.dialog.open)) return;
      this.startButton.disabled = false;
      if (!privacyReady || !this.privacyAllowed()) {
        this.showConsent(true);
        if (this.cookieSettings) this.cookieSettings.hidden = typeof window.privacyBanner?.showPreferences !== 'function';
        this.setStatus(this.dataset.blockedMessage);
        const nextAction = this.cookieSettings && !this.cookieSettings.hidden ? this.cookieSettings : this.querySelector('[data-tv-cancel]');
        this.focusConsentAction(nextAction);
        return;
      }
      if (!customElements.get('deferred-media')) {
        this.unload(false);
        this.setStatus(this.dataset.unavailableMessage);
        return;
      }
      document.querySelectorAll('shop-tv-player').forEach(player => {
        if (player !== this) player.unload(false);
      });
      try {
        this.media.loadContent(true);
      } catch {
        this.unload(false);
        this.setStatus(this.dataset.unavailableMessage);
        return;
      }
      if (this.poster) this.poster.hidden = true;
      this.showConsent(false);
      if (this.withdrawButton) this.withdrawButton.hidden = false;
      if (this.cookieSettings) this.cookieSettings.hidden = true;
      this.playRequested = false;
      this.setStatus(this.dataset.readyMessage);
    }

    unload(focus, preserveConsent = false) {
      if (!preserveConsent) {
        if (pendingConsent?.player === this) clearConsentIntent();
        this.playRequested = false;
      }
      this.generation = (this.generation || 0) + 1;
      this.media?.querySelectorAll(':scope > iframe, :scope > video').forEach(player => {
        if (player.tagName === 'VIDEO') player.pause();
        player.remove();
      });
      this.media?.removeAttribute('loaded');
      if (this.poster) this.poster.hidden = false;
      this.showConsent(false);
      if (this.withdrawButton) this.withdrawButton.hidden = true;
      if (this.cookieSettings) this.cookieSettings.hidden = true;
      if (this.startButton) this.startButton.disabled = false;
      this.setStatus('');
      if (focus) this.startButton?.focus({ preventScroll: true });
    }
  }

  customElements.define('shop-tv-player', ShopTvPlayer);
})();
